  import { HttpClient, HttpHeaders, HttpParams, HttpEvent, HttpProgressEvent } from '@angular/common/http';
  import { Injectable, inject, Inject } from '@angular/core';
  import { EnvConfig, Environment } from '@doc/core';
  import { Observable, throwError, firstValueFrom, of, BehaviorSubject } from 'rxjs';
  import { ApiUrls } from '../document-urls';
  import { catchError, map, shareReplay } from 'rxjs/operators';
  import { UploadManagerService } from './upload-manager.service';
  import { BaseResponse } from '../models/api.model';

  export interface ResumableUploadSession {
    sessionId?: string;
    userId: number;
    fileName: string;
    fileSize: number;
    contentType: string;
    uploadUrl?: string;
    chunkSize: number;
    bytesUploaded?: number;
    status?: string;
    folderId?: number;
    libraryType?: number;
    departmentId?: number;
    titleEn?: string;
    titleAr?: string;
    eventId?: number;
    eventDate?: string;
    eventStartDate?: string;
    eventEndDate?: string;
    locationId?: number;
    newLocation?: string;
    tagIds?: string;
    newTags?: string;
    newEvent?: string;
    isMarkSecure?: boolean;
    userEmails?: string;
    userIds?: number[];
    emails?: string[];
    expireOn?: string;
    shareType?: 'internal' | 'external';
    fileCreationDate?: string;
    totalChunks?: number;
    completedChunks?: number;
    failedChunks?: number;
    completedChunkIndices?: number[]; // Array of completed chunk indices
    failedChunkIndices?: any[]; // Array of failed chunks with retry info
    expiresOn?: string;
    filePath?: string;
    isResume?: boolean;
    /** Optional approval workflow (same as document create / normal upload). */
    workflowId?: number;
    /** True for media-library uploads (vs Document Page created docs). */
    IsUploaded?: boolean;
  }

  export interface UploadProgress {
    sessionId: string;
    bytesUploaded: number;
    totalBytes: number;
    progressPercentage: number;
    status: string;
  }

  export interface ChunkUploadResponse {
    chunkIndex: number;
    bytesUploaded: number;
    progress: number;
  }

  @Injectable({
    providedIn: 'root',
  })
  export class ResumableUploadService {
    private http = inject(HttpClient);
    private apiUrl: string;
    private uploadManager?: UploadManagerService;
    private readonly allowedOrigin: string;
    
    // Track incomplete upload count
    private incompleteUploadCountSubject = new BehaviorSubject<number>(0);
    public incompleteUploadCount$: Observable<number> = this.incompleteUploadCountSubject.asObservable();
    
    // Cache for incomplete uploads to avoid duplicate calls
    private incompleteUploadsCache$: Observable<ResumableUploadSession[]> | null = null;
    private cacheTimestamp: number = 0;
    private readonly CACHE_DURATION = 5000; // 5 seconds cache
    private readonly skipLoaderHeaders = new HttpHeaders({ 'X-Skip-Loader': 'true' });

    constructor(@Inject(EnvConfig) private config: Environment) {
      this.apiUrl = this.config.apiUrl;
      // Get origin from window.location or use configured frontend URL
      // This ensures we always send the correct Origin header for CORS
      if (typeof window !== 'undefined' && window.location) {
        this.allowedOrigin = window.location.origin;
      } else {
        // Fallback: use configured frontend URL or default
        this.allowedOrigin = (this.config as any).frontendUrl || 'https://media-dev.aspirezone.qa';
      }
    }

    /**
     * Set upload manager for accessing connection speed info
     */
    setUploadManager(uploadManager: UploadManagerService): void {
      this.uploadManager = uploadManager;
    }

    /**
     * Calculate timeout for chunk upload based on chunk size and connection speed
     */
    private calculateChunkTimeout(chunkSize: number): number {
      const config = this.config.resumableUpload;
      if (!config) {
        return 60000; // Default 60 seconds
      }

      // Base timeout from config or default
      const baseTimeout = config.chunkUploadTimeout || 60000; // Default 60 seconds
      
      // Get timeout multiplier based on connection speed
      let multiplier = 1;
      if (this.uploadManager) {
        const speed = this.uploadManager.connectionSpeed;
        const multipliers = config.timeoutMultipliers || {};
        
        switch (speed) {
          case 'slow':
            multiplier = multipliers.slow || 5; // 5x for slow connections
            break;
          case 'medium':
            multiplier = multipliers.medium || 3; // 3x for medium connections
            break;
          case 'fast':
            multiplier = multipliers.fast || 2; // 2x for fast connections
            break;
          case 'veryFast':
            multiplier = multipliers.veryFast || 1.5; // 1.5x for very fast connections
            break;
        }
      }

      // Calculate timeout: base timeout * multiplier
      // Also add extra time based on chunk size (for very large chunks on slow connections)
      const chunkSizeMB = chunkSize / (1024 * 1024);
      const sizeBasedExtra = chunkSizeMB * 2000; // 2 seconds per MB for very slow connections
      
      let timeout = baseTimeout * multiplier + sizeBasedExtra;

      // Apply min/max bounds
      const minTimeout = config.minTimeout || 60000; // Minimum 60 seconds - increased for slow internet
      const maxTimeout = config.maxTimeout || 1200000; // Maximum 20 minutes - increased for slow internet
      
      timeout = Math.max(minTimeout, Math.min(timeout, maxTimeout));

      return Math.round(timeout);
    }

    /**
     * Initialize a resumable upload session
     */
    initializeUpload(session: ResumableUploadSession): Observable<any> {
      const link = this.apiUrl + ApiUrls.initializeResumableUpload;
      return this.http.post<any>(link, session, { headers: this.skipLoaderHeaders }).pipe(
        catchError((error) => {
          console.error('Error initializing upload:', error);
          return throwError(() => error);
        })
      );
    }

    /**
     * Upload a chunk through backend API (proxies to GCS)
     * @returns Number of bytes uploaded (from Range header response) - source of truth
     */
    /**
     * Upload chunk through backend proxy (separate from direct GCS upload)
     * This method sends FormData to the backend, which then proxies to GCS
     * Progress is handled in the UI based on the bytesUploaded returned from backend
     */
    async uploadChunkThroughBackend(
      uploadUrl: string,
      chunk: ArrayBuffer,
      startByte: number,
      endByte: number,
      totalSize: number,
      abortSignal?: AbortSignal,
      sessionId?: string,
      chunkIndex?: number
    ): Promise<number> {
      const chunkSize = endByte - startByte + 1;
      const chunkNumber = Math.floor(startByte / chunkSize) + 1;
      const totalChunks = Math.ceil(totalSize / chunkSize);

      // CRITICAL: Validate chunk data before creating Blob
      if (!chunk || chunk.byteLength === 0) {
        throw new Error(`Chunk ${chunkIndex ?? chunkNumber} is empty or null. Cannot upload empty chunk.`);
      }

      // Validate chunk size matches expected size
      if (chunk.byteLength !== chunkSize) {
        console.warn(`[BACKEND CHUNK ${chunkNumber}/${totalChunks}] Chunk size mismatch: expected ${chunkSize} bytes, got ${chunk.byteLength} bytes. This may cause upload errors.`);
        // For last chunk, it's OK if chunk is smaller (file might not be exact multiple of chunkSize)
        const isLastChunk = endByte >= totalSize - 1;
        if (!isLastChunk && chunk.byteLength < chunkSize) {
          throw new Error(`Chunk ${chunkIndex ?? chunkNumber} size mismatch: expected ${chunkSize} bytes, got ${chunk.byteLength} bytes.`);
        }
      }

      const blob = new Blob([chunk], { type: 'application/octet-stream' });

      console.log(`[BACKEND CHUNK ${chunkNumber}/${totalChunks}] Uploading through backend (${(chunkSize / 1024 / 1024).toFixed(2)} MB, actual chunk size: ${(chunk.byteLength / 1024 / 1024).toFixed(2)} MB)`);

      try {
        // Check if aborted before starting
        if (abortSignal?.aborted) {
          throw new Error('Upload cancelled');
        }

        // Send chunk as stream in request body with metadata in query parameters
        const headers = new HttpHeaders({
          'Content-Type': 'application/octet-stream',
          'X-Skip-Loader': 'true'
        });

        let params = new HttpParams()
          .set('uploadUrl', uploadUrl)
          .set('startByte', startByte.toString())
          .set('endByte', endByte.toString())
          .set('totalSize', totalSize.toString());
        
        if (sessionId) {
          params = params.set('sessionId', sessionId);
        }
        if (chunkIndex !== undefined) {
          params = params.set('chunkIndex', chunkIndex.toString());
        }

        // CRITICAL: Final validation - ensure blob is not empty before sending
        if (blob.size === 0) {
          throw new Error(`Cannot upload empty chunk ${chunkIndex ?? chunkNumber}: blob size is 0 (startByte=${startByte}, endByte=${endByte}, chunkSize=${chunkSize})`);
        }

        // Send to backend - chunk data in body, metadata in query parameters
        const response = await firstValueFrom(
          this.http.post<any>(this.apiUrl + ApiUrls.uploadChunk, blob, {
            headers: headers,
            params: params,
            reportProgress: false
          }).pipe(
            catchError((error) => {
              if (abortSignal?.aborted) {
                throw new Error('Upload cancelled');
              }
              
              // Handle HTTP error responses (4xx, 5xx)
              if (error.status === 404 || error.status === 410) {
                const expiredError: any = new Error('UPLOAD_URL_EXPIRED');
                expiredError.isUrlExpired = true;
                expiredError.status = error.status;
                throw expiredError;
              }

              if (error.error) {
                const errorData = error.error?.data || error.error;
                const statusCode = errorData?.statusCode || errorData?.StatusCode;
                const errorMessage = errorData?.errorMessage || errorData?.ErrorMessage || error.message;
                
                console.error(`[BACKEND CHUNK ${chunkNumber}/${totalChunks}] Backend upload error: ${errorMessage} (HTTP Status: ${error.status}, Response Status: ${statusCode})`);
                throw new Error(`Backend upload failed: ${errorMessage}`);
              }
              
              console.error(`[BACKEND CHUNK ${chunkNumber}/${totalChunks}] Network error:`, error);
              throw new Error(`Network error: ${error.message || error}`);
            })
          )
        );

        // Check if aborted after request completes
        if (abortSignal?.aborted) {
          throw new Error('Upload cancelled');
        }

        // Extract response data
        // Backend returns BaseResponse with data containing statusCode, bytesUploaded, headers
        const responseData = response?.data || response;
        const statusCode = responseData?.statusCode || responseData?.StatusCode || 200;
        const bytesUploaded = responseData?.bytesUploaded || responseData?.BytesUploaded || 0;
        const rangeHeader = responseData?.headers?.Range || responseData?.headers?.range || 
                           responseData?.Headers?.Range || responseData?.Headers?.range;

        // ✅ 200/201 = Upload COMPLETE
        if (statusCode === 200 || statusCode === 201) {
          console.log(`[BACKEND CHUNK ${chunkNumber}/${totalChunks}] ✅ Upload COMPLETE (status ${statusCode})`);
          return totalSize; // Return total size to indicate completion
        }

        // ✅ 308 = SUCCESS (chunk accepted, more chunks needed)
        // This is NOT an error - it means the chunk was accepted and upload is in progress
        if (statusCode === 308) {
          let nextByte = bytesUploaded;
          
          // Parse Range header if available (more accurate than bytesUploaded)
          if (rangeHeader) {
            const match = rangeHeader.match(/bytes\s*=\s*0\s*-\s*(\d+)/i);
            if (match && match[1]) {
              const lastUploadedByte = parseInt(match[1], 10);
              nextByte = lastUploadedByte + 1; // Next byte = lastByte + 1 (end is inclusive)
            }
          }
          
          // If bytesUploaded is 0 but we have a range header, use range header
          if (nextByte === 0 && rangeHeader) {
            const match = rangeHeader.match(/bytes\s*=\s*0\s*-\s*(\d+)/i);
            if (match && match[1]) {
              nextByte = parseInt(match[1], 10) + 1;
            }
          }
          
          // Fallback: use endByte + 1 if bytesUploaded is still 0
          if (nextByte === 0) {
            nextByte = endByte + 1;
          }
          
          console.log(`[BACKEND CHUNK ${chunkNumber}/${totalChunks}] ✅ Accepted (308). Next byte: ${nextByte.toLocaleString()}/${totalSize.toLocaleString()}`);
          return nextByte; // Return next byte position for progress tracking
        }

        // ❌ 4xx/5xx = Error
        if (statusCode >= 400) {
          throw new Error(`Chunk upload failed with status ${statusCode}`);
        }

        // ⚠️ Unexpected status code - use fallback
        console.warn(`[BACKEND CHUNK ${chunkNumber}/${totalChunks}] Unexpected status code: ${statusCode}. Using fallback.`);
        return bytesUploaded > 0 ? bytesUploaded : endByte + 1;
        
      } catch (error: any) {
        if (error.name === 'AbortError' || error.message?.includes('cancelled')) {
          throw error;
        }
        console.error(`[BACKEND CHUNK ${chunkNumber}/${totalChunks}] Upload failed:`, error);
        throw new Error(`Backend chunk upload failed: ${error.message || error}`);
      }
    }

    /**
     * Upload a chunk directly to GCS and return the actual bytes uploaded (from Range header)
     * @returns Number of bytes uploaded (from Range header response) - source of truth
     */
    async uploadChunkToGCS(
      uploadUrl: string,
      chunk: ArrayBuffer,
      startByte: number,
      endByte: number,
      totalSize: number,
      abortSignal?: AbortSignal,
      sessionId?: string,
      chunkIndex?: number
    ): Promise<number> {
      const blob = new Blob([chunk], { type: 'application/octet-stream' });
      const chunkSize = endByte - startByte + 1;
      const chunkNumber = Math.floor(startByte / chunkSize) + 1;
      const totalChunks = Math.ceil(totalSize / chunkSize);
      
      // Calculate timeout based on chunk size and connection speed
      const timeout = this.calculateChunkTimeout(chunkSize);
      
      // Create timeout abort controller
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => {
        timeoutController.abort();
      }, timeout);

      // Combine abort signals (user abort + timeout)
      const combinedSignal = abortSignal 
        ? this.combineAbortSignals([abortSignal, timeoutController.signal])
        : timeoutController.signal;

      try {
        // Get current origin for logging (browser automatically sets this in Origin header)
        const currentOrigin = typeof window !== 'undefined' ? window.location.origin : this.allowedOrigin;
        console.log(`[CHUNK ${chunkNumber}/${totalChunks}] Uploading chunk (${(chunkSize / 1024 / 1024).toFixed(2)} MB) with timeout: ${(timeout / 1000).toFixed(1)}s`);
        console.log(`[CHUNK] Origin: ${currentOrigin} (browser will send this in Origin header for CORS)`);
   
        // ✅ CORS Configuration:
        // - Browser automatically sets Origin header from window.location.origin
        // - GCS bucket must have CORS configured to allow this origin: https://media-dev.aspirezone.qa
        // - We use mode: 'cors' to ensure proper CORS handling
        // - Origin must match exactly in GCS CORS config (no trailing slash)
        const headers: HeadersInit = {
          'Content-Range': `bytes ${startByte}-${endByte}/${totalSize}`,
          'Content-Type': 'application/octet-stream'
          // Note: Origin header is automatically set by browser - cannot be manually set
        };
        
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: headers,
          body: blob,
          signal: combinedSignal,
          redirect: 'manual',
          mode: 'cors', // ✅ Explicitly use CORS mode - ensures proper CORS handling
          credentials: 'omit' // Don't send cookies to GCS
        });

        // Clear timeout on success
        clearTimeout(timeoutId);
        
        if (response.type === 'opaque' || response.status === 0) {
          console.warn(`[CHUNK ${chunkNumber}/${totalChunks}] Opaque response or status 0 detected. This might be a 308. Using fallback.`);
          return endByte + 1; // Continue with next chunk
        }

      
        if (response.status === 200 || response.status === 201) {
          console.log(`[CHUNK ${chunkNumber}/${totalChunks}] ✅ Upload COMPLETE (status ${response.status}). All ${totalChunks} chunks uploaded successfully.`);
          return totalSize; // Return total size - upload is complete
        }

        if (response.status === 308) {
          const rangeHeader = response.headers.get('Range') || response.headers.get('range'); // Format: "bytes=0-xxxxx" (case-insensitive)
          
          if (rangeHeader) {
            // Parse Range header: "bytes=0-5242879" -> next byte is 5242880
            // Match patterns: "bytes=0-123" or "bytes=0-1234567"
            const match = rangeHeader.match(/bytes\s*=\s*0\s*-\s*(\d+)/i);
            if (match && match[1]) {
              const lastUploadedByte = parseInt(match[1], 10);
              const nextByteToUpload = lastUploadedByte + 1; // ✅ Next byte = lastByte + 1 (end is inclusive)
              
              console.log(`[CHUNK ${chunkNumber}/${totalChunks}] ✅ Accepted (308 - SUCCESS). Range: ${rangeHeader}, Next byte: ${nextByteToUpload.toLocaleString()}/${totalSize.toLocaleString()} bytes`);
              
              // Verify Range header makes sense - next byte should be >= startByte (we might have already uploaded more)
              if (nextByteToUpload < startByte) {
                console.warn(`[CHUNK] Range header shows ${nextByteToUpload} bytes, but we started at ${startByte}. Using Range header value.`);
              }
              
              return nextByteToUpload; // Return next byte to upload (source of truth from GCS)
            } else {
              console.warn(`[CHUNK ${chunkNumber}/${totalChunks}] ✅ Accepted (308) but Range header format unexpected: ${rangeHeader}`);
            }
          }
          
          // If no Range header, send status query request to get current progress
          console.log(`[CHUNK ${chunkNumber}/${totalChunks}] ✅ Accepted (308) but no Range header. Querying upload status...`);
          try {
            const statusBytes = await this.getGCSUploadProgress(uploadUrl, totalSize);
            if (statusBytes > 0) {
              console.log(`[CHUNK] Status query returned: ${statusBytes.toLocaleString()} bytes uploaded`);
              return statusBytes;
            }
          } catch (statusError) {
            console.warn(`[CHUNK] Status query failed: ${statusError}. Using endByte + 1 as fallback.`);
          }
          
          // Final fallback: use endByte + 1
          const fallbackBytes = endByte + 1;
          console.warn(`[CHUNK ${chunkNumber}/${totalChunks}] Using fallback: ${fallbackBytes.toLocaleString()} bytes`);
          return fallbackBytes;
        }

        // ✅ Handle status 300 (Multiple Choices) - treat as CORS-type failure if it's the last chunk
        // This is a special case where we should call Verify immediately
        if (response.status === 300) {
          // Check if this is the last chunk
          const isLastChunk = endByte >= totalSize - 1;
          if (isLastChunk) {
            console.log(`[CHUNK ${chunkNumber}/${totalChunks}] Status 300 on last chunk - treating as success, will verify`);
            return totalSize; // Return total size to indicate completion
          }
          // For non-last chunks, treat as error
          const errorText = await response.text().catch(() => 'Status 300');
          throw new Error(`Chunk upload failed: ${response.status} - ${errorText}`);
        }

        // Handle authentication/authorization errors (expired or invalid URL)
        if (response.status === 401 || response.status === 403) {
          const errorText = await response.text().catch(() => 'Unauthorized');
          throw new Error('Upload URL expired or invalid. Please re-initialize the session.');
        }

        // Handle other HTTP errors
        // ✅ IMPORTANT: Only throw for actual errors (4xx, 5xx)
        // Do NOT throw for 308 - it's already handled above as SUCCESS
        // Do NOT throw for 0 status with redirect: 'manual' - it might be a 308 that we couldn't read
        if (response.status >= 400) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`Chunk upload failed: ${response.status} - ${errorText}`);
        }
        
        // If we reach here with an unexpected status code (not 200, 201, 308, 300, 401, 403, or 4xx/5xx)
        // Log it but don't fail - might be a browser quirk
        console.warn(`[CHUNK ${chunkNumber}/${totalChunks}] Unexpected status code: ${response.status}. Treating as success and continuing.`);
        return endByte + 1; // Continue with next chunk
      } catch (error: any) {
        // Clear timeout on error
        clearTimeout(timeoutId);
        
        // Handle abort signal - don't retry if aborted
        if (error.name === 'AbortError' || abortSignal?.aborted) {
          throw new Error('Upload cancelled');
        }
        
        // Handle timeout specifically
        if (error.name === 'AbortError' && timeoutController.signal.aborted) {
          throw new Error(`Chunk upload timeout after ${(timeout / 1000).toFixed(1)}s. The connection may be too slow for this chunk size.`);
        }
        
        // Re-throw if it's already our formatted error
        if (error.message && (error.message.includes('expired') || error.message.includes('failed') || error.message.includes('Chunk upload') || error.message.includes('timeout'))) {
          throw error;
        }
        // Network or other errors
        throw new Error(`Network error uploading chunk: ${error.message || 'Unknown error'}`);
      }
    }

    /**
     * Combine multiple AbortSignals into one
     */
    private combineAbortSignals(signals: AbortSignal[]): AbortSignal {
      const controller = new AbortController();
      
      signals.forEach(signal => {
        if (signal.aborted) {
          controller.abort();
        } else {
          signal.addEventListener('abort', () => controller.abort());
        }
      });
      
      return controller.signal;
    }

    private parseRangeHeader(rangeHeader: string): number {
      const match = rangeHeader.match(/bytes=0-(\d+)/);
      if (match && match[1]) {
        return parseInt(match[1], 10) + 1; // +1 because end is inclusive
      }
      return 0;
    }

  
    async getGCSUploadProgress(uploadUrl: string, totalSize: number): Promise<number> {
      try {
        // ✅ Only send essential headers - NO x-goog-meta headers
        // ✅ Use CORS mode to ensure proper CORS handling
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Range': `bytes */${totalSize}`,
            'Content-Length': '0'
            // ✅ DO NOT send x-goog-meta-* headers
          },
          mode: 'cors', // ✅ Explicitly use CORS mode
          credentials: 'omit' // Don't send cookies to GCS
        });

        // ✅ 308 Resume Incomplete = SUCCESS (upload in progress, NOT an error)
        // Range header contains bytes uploaded
        if (response.status === 308) {
          const rangeHeader = response.headers.get('Range') || response.headers.get('range'); // Case-insensitive
          if (rangeHeader) {
            // Parse Range header: "bytes=0-1234567" -> next byte is 1234568 (lastByte + 1)
            const match = rangeHeader.match(/bytes\s*=\s*0\s*-\s*(\d+)/i);
            if (match && match[1]) {
              const lastUploadedByte = parseInt(match[1], 10);
              const nextByteToUpload = lastUploadedByte + 1; // ✅ Next byte = lastByte + 1 (end is inclusive)
              return nextByteToUpload;
            }
          }
          // 308 without Range header means upload not started yet
          return 0;
        }

        // 200 OK or 201 Created - upload is complete
        if (response.status === 200 || response.status === 201) {
          return totalSize;
        }

        // 401/403 - URL expired or invalid (should re-initialize)
        if (response.status === 401 || response.status === 403) {
          console.warn('Upload URL expired while checking progress');
          return 0;
        }

        // 503 Service Unavailable - GCS is temporarily unavailable, retry after delay
        if (response.status === 503) {
          console.warn('GCS returned 503 Service Unavailable while checking progress. This may be temporary.');
          // Return -1 to indicate we should retry or use stored value
          return -1;
        }

        // Other status codes - log and return 0
        console.warn(`Unexpected status ${response.status} when checking progress`);
        return 0;
      } catch (error) {
        // Network errors - silently fail (local progress tracking is primary)
        console.debug('Could not query GCS progress (network error):', error);
        return 0;
      }
    }

    verifyUpload(filePath: string, expectedSize: number, sessionId?: string): Observable<any> {
      const link = this.apiUrl + ApiUrls.verifyResumableUpload;
      return this.http.post<any>(
        link,
        {
          filePath: filePath,
          expectedSize: expectedSize,
          sessionId: sessionId
        },
        { headers: this.skipLoaderHeaders }
      ).pipe(
        catchError((error) => {
          console.error('Error verifying upload:', error);
          return throwError(() => error);
        })
      );
    }

    
    getIncompleteUploads(forceRefresh: boolean = false): Observable<ResumableUploadSession[]> {
      const now = Date.now();
      
      // Return cached result if available and not expired, unless force refresh
      if (!forceRefresh && this.incompleteUploadsCache$ && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
        return this.incompleteUploadsCache$;
      }
      
      // Create new request and cache it
      const link = this.apiUrl + ApiUrls.getIncompleteResumableUploads;
      this.incompleteUploadsCache$ = this.http.get<BaseResponse & { data?: ResumableUploadSession[] }>(link, {
        headers: this.skipLoaderHeaders,
      }).pipe(
        map(response => {
          const sessions = response.data || [];
          // Update incomplete upload count
          this.incompleteUploadCountSubject.next(sessions.length);
          // Parse JSON strings to arrays if needed
          return sessions.map(s => ({
            ...s,
            completedChunkIndices: typeof s.completedChunkIndices === 'string' 
              ? JSON.parse(s.completedChunkIndices) 
              : s.completedChunkIndices || [],
            failedChunkIndices: typeof s.failedChunkIndices === 'string'
              ? JSON.parse(s.failedChunkIndices)
              : s.failedChunkIndices || []
          }));
        }),
        catchError((error) => {
          console.error('Error getting incomplete uploads:', error);
          this.incompleteUploadCountSubject.next(0);
          return of([]);
        }),
        shareReplay(1) // Share the result among all subscribers
      );
      
      this.cacheTimestamp = now;
      return this.incompleteUploadsCache$;
    }
    
    /**
     * Manually update incomplete upload count (e.g., after resuming or completing)
     */
    updateIncompleteUploadCount(count: number): void {
      this.incompleteUploadCountSubject.next(count);
    }
    
    /**
     * Invalidate cache (call after upload completes or is cancelled)
     */
    invalidateIncompleteUploadsCache(): void {
      this.incompleteUploadsCache$ = null;
      this.cacheTimestamp = 0;
    }
    
    /**
     * Get current incomplete upload count
     */
    getIncompleteUploadCount(): number {
      return this.incompleteUploadCountSubject.value;
    }

   
    getPendingChunks(sessionId: string): Observable<number[]> {
      const link = this.apiUrl + ApiUrls.getPendingChunks + `/${sessionId}/pending-chunks`;
      return this.http.get<BaseResponse & { data?: number[] }>(link, { headers: this.skipLoaderHeaders }).pipe(
        map(response => response.data || []),
        catchError((error) => {
          console.error('Error getting pending chunks:', error);
          return of([]);
        })
      );
    }

    /**
     * Cancel/delete upload session - mark as Cancelled in database
     */
    pauseSession(sessionId: string): Observable<BaseResponse> {
      const link = `${this.apiUrl}${ApiUrls.pauseResumableUpload}/${sessionId}/pause`;
      return this.http.post<BaseResponse>(link, {}, { headers: this.skipLoaderHeaders }).pipe(
        catchError((error) => {
          console.error('Error pausing upload session:', error);
          return throwError(() => error);
        })
      );
    }

    registerUploadSessionsBatch(sessions: ResumableUploadSession[]): Observable<any> {
      const link = this.apiUrl + ApiUrls.registerResumableUploadBatch;
      return this.http.post<any>(link, sessions, { headers: this.skipLoaderHeaders }).pipe(
        catchError((error) => {
          console.error('Error batch-registering upload sessions:', error);
          return throwError(() => error);
        })
      );
    }

    cancelUpload(sessionId: string): Observable<BaseResponse> {
      const link = this.apiUrl + `/api/ResumableUpload/session/${sessionId}/cancel`;
      return this.http.post<BaseResponse>(link, {}, { headers: this.skipLoaderHeaders }).pipe(
        catchError((error) => {
          console.error('Error cancelling upload session:', error);
          return throwError(() => error);
        })
      );
    }

    /**
     * Complete upload - move from resumableuploadsession to File table
     */
    completeUpload(sessionId: string): Observable<BaseResponse> {
      const link = this.apiUrl + `/api/ResumableUpload/session/${sessionId}/complete`;
      return this.http.post<BaseResponse>(link, {}, { headers: this.skipLoaderHeaders }).pipe(
        catchError((error) => {
          console.error('Error completing upload session:', error);
          return throwError(() => error);
        })
      );
    }

  }



